import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import endConsumerV2 from '../../utils/api/end-consumer-v2';
import BatteryLoader from '../battery-loader/BatteryLoader';

interface PaymentDetailsProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

interface PaymentDetailsDataProperties {
  _id: string;
  userCustomId: string;
  createdAt: string;

  amount: string;
  currency: string;
  status: string;
  paymentType: string;
  descriptor: string;
  hyperpayTransactionId: string;
  hyperpayTimestamp: string;
  updatedAt: string;
  isGuestUser: boolean;

  result: {
    code: string;
    description?: string;
  };

  risk: {
    score: number;
  };

  hyperpayCustomerCardDetails: {
    _id: string;
    token: string;
    paymentBrand: string;
    last4Digits?: string;
    expiryMonth?: string;
    expiryYear?: string;
    cardHolder?: string;
  };

  resultDetails?: {
    ConnectorTxID1?: string;
    ConnectorTxID2?: string;
    connectorId?: string;
  };
}

/**
 * Station side panel
 * @param {any} properties Component properties
 * @returns {any} Payment details component
 */
function PaymentDetails(properties: PaymentDetailsProperties) {
  const { fetchIdFrom, id } = properties;
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [transactionData, setTransactionData] = useState<PaymentDetailsDataProperties[]>([]);

  const transactionMap = [
    {
      header: `${transactionData?.[0]?.amount || '0.00'} ${transactionData?.[0]?.currency || ''}`,
      keyValuePair: [
        {
          label: 'Charged to',
          key: 'userCustomId',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom view
           */
          view: (value: any) => {
            return (
              <div className="transaction-item">
                <h2>Charged To:</h2>
                <p>{value?.[0]?.userCustomId}</p>
              </div>
            );
          },
        },
      ],
    },
    {
      header: 'Recent activity',
      keyValuePair: [
        {
          label: 'Payment Authorized:',
          key: 'createdAt',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom view
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>Payment Authorized:</h2>
              <p>
                {ConvertUTCtoDate(value?.[0]?.createdAt)}{' '}
                {FormatUtcString(ConvertUTCtoHHMM(value?.[0]?.createdAt))}
              </p>
            </div>
          ),
        },
        {
          label: 'Payment Started:',
          key: 'createdAt',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom view
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>Payment Started:</h2>
              {value?.[0]?.hyperpayTimestamp ? (
                <p>
                  {ConvertUTCtoDate(value?.[0]?.hyperpayTimestamp)}{' '}
                  {FormatUtcString(ConvertUTCtoHHMM(value?.[0]?.hyperpayTimestamp))}
                </p>
              ) : (
                <p>—</p>
              )}
            </div>
          ),
        },
      ],
    },
    {
      header: 'Payment breakdown',
      keyValuePair: [
        {
          label: 'Payment amount',
          key: 'amount',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom view
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>Payment Amount:</h2>
              <p>{value?.[0]?.amount}</p>
            </div>
          ),
        },
        {
          label: 'Stripe processing fees',
          key: 'penaltyDetails.ceilingValue',
        },
        {
          label: 'Net amount',
          key: 'penaltyDetails.ceilingValue',
        },
      ],
    },
    {
      header: 'Payment method',
      keyValuePair: [
        {
          label: 'Id',
          key: 'penaltyDetails.ceilingValue',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom view
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>Id:</h2>
              <p>{value?.[0]?.hyperpayTransactionId}</p>
            </div>
          ),
        },
        {
          label: 'Number',
          key: 'hyperpayCustomerCardDetails?.card?.last4Digits',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom view
           */
          view: (value: any) => {
            console.log('value:', value);
            return (
              <div className="transaction-item">
                <h2>Number:</h2>
                <p>•••• •••• •••• {value?.[0]?.hyperpayCustomerCardDetails?.card?.last4Digits}</p>
              </div>
            );
          },
        },
        {
          label: 'Fingerprint',
          key: 'penaltyDetails.ceilingValue',
        },
        {
          label: 'Expires',
          key: 'expiryMonth',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom view
           */
          view: (value: any) => {
            return (
              <div className="transaction-item">
                <h2>Expires:</h2>
                <p>
                  {value?.[0]?.hyperpayCustomerCardDetails?.card?.expiryMonth}/
                  {value?.[0]?.hyperpayCustomerCardDetails?.card?.expiryYear}
                </p>
              </div>
            );
          },
        },
        {
          label: 'Type',
          key: 'penaltyDetails.ceilingValue',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom view
           */
          view: (value: any) => {
            return (
              <div className="transaction-item">
                <h2>Type:</h2>
                <p>{value?.[0]?.hyperpayCustomerCardDetails?.paymentBrand}</p>
              </div>
            );
          },
        },
        {
          label: 'Issuer',
          key: 'penaltyDetails.ceilingValue',
        },
        {
          label: 'Owner',
          key: 'penaltyDetails.ceilingValue',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom view
           */
          view: (value: any) => {
            return (
              <div className="transaction-item">
                <h2>Owner:</h2>
                <p>{value?.[0]?.hyperpayCustomerCardDetails?.card?.holder}</p>
              </div>
            );
          },
        },
        {
          label: 'Address',
          key: 'penaltyDetails.ceilingValue',
        },
        {
          label: 'Origin',
          key: 'penaltyDetails.ceilingValue',
        },
      ],
    },
    {
      header: 'Events',
      keyValuePair: [
        {
          label: 'Payment amount:',
          key: 'penaltyDetails.ceilingValue',
        },
        {
          label: 'Stripe processing fees:',
          key: 'penaltyDetails.ceilingValue',
        },
        {
          label: 'Net amount:',
          key: 'penaltyDetails.ceilingValue',
        },
      ],
    },
  ];

  /**
   * Function to fetch station details
   * @param {any} transactionId transaction id
   */
  const handleFetchStationDetails = async (transactionId: string) => {
    try {
      setIsLoading(true);

      const response = await endConsumerV2.fetchPaymentDetailsSidePanel(transactionId);
      setTransactionData(response.data);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || 'Something went wrong!',
          type: 'error',
          code: 0,
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Function to get nested values from transactionData using dot notation
   * @param {any} path dot-separated key path
   * @param {any} utcToDate true if time needs formatting
   * @returns {any} value or undefined
   */
  const getNestedValue = (path: string, utcToDate: boolean): any => {
    let value;
    try {
      value = path
        .split('.')
        .reduce((accumulator, key) => accumulator?.[key], transactionData?.[0] as any);
      if (utcToDate && value) {
        value = `${ConvertUTCtoDate(value)} - ${FormatUtcString(ConvertUTCtoHHMM(value))}`;
      }
    } catch (error) {
      console.error(error);
    }
    return value;
  };

  useEffect(() => {
    let transactionObjectId;
    if (fetchIdFrom === 'queryParams') {
      transactionObjectId = searchParameters.get('paymentDetailsId');
    } else if (fetchIdFrom === 'props') {
      transactionObjectId = id;
    }

    if (!transactionObjectId) {
      return;
    }

    handleFetchStationDetails(transactionObjectId);
  }, [searchParameters, id]);

  return (
    <div className="transaction-side-panel-wrap">
      {isLoading ? (
        <div className="loader">
          <BatteryLoader />
        </div>
      ) : (
        <div className="transaction-side-panel-container">
          {transactionMap.map((section) => (
            <div className="transaction-section">
              <h1>{section.header}</h1>
              {section.keyValuePair.map((item: any) => {
                const value = getNestedValue(item.key, item.date || false);
                return item.view ? (
                  item.view(transactionData)
                ) : (
                  <div className="transaction-item">
                    <h2>{item.label}: </h2>
                    <p>{value !== undefined && value !== null ? String(value) : '—'}</p>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PaymentDetails;
